// Live, grouped requisition listings rendered from the database.
// Used on /careers (consolidated page). Extracted from the former
// /careers/jobs PublicJobsPage so the listings can be embedded inside
// the careers narrative without a second page.

import { useEffect, useMemo, useState } from 'react';
import { Link } from '@/lib/router-compat';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Briefcase, MapPin, ArrowRight, Shield } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { LoadErrorState } from '@/components/shared/LoadErrorState';
import { formatPayRange } from '@/lib/postingPay';
import {
  POSTING_TYPE_ORDER,
  getPostingMeta,
  type PostingType,
} from '@/lib/postingTypes';

type Job = {
  id: string;
  slug: string;
  title: string;
  location: string | null;
  location_type: string | null;
  employment_type: string | null;
  clearance_required: string | null;
  position_overview: string | null;
  published_at: string | null;
  posting_type: PostingType | null;
  contingency_note: string | null;
  expected_start_window: string | null;
  public_pay_min?: number | string | null;
  public_pay_max?: number | string | null;
  public_pay_interval?: string | null;
};

const SECTION_BLURBS: Record<PostingType, string> = {
  active:
    'Funded roles we are actively interviewing for. Standard application path.',
  contingent:
    'Roles contingent on contract award. Apply now to be considered when staffed.',
  forecasted:
    'Anticipated needs based on our contract pipeline. Pre-qualified candidates are contacted first.',
  evergreen:
    'Disciplines we recruit across continuously. Submit interest to join the talent pool.',
};

export const PublicJobsList = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(false);
    (async () => {
      try {
        const { data, error } = await supabase.rpc('get_public_job_postings');
        if (error) throw error;
        if (active) setJobs((data as Job[]) || []);
      } catch (error) {
        if (active) {
          logger.error('Failed to load public jobs', error);
          setLoadError(true);
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [attempt]);

  const byType = useMemo(() => {
    const grouped: Record<PostingType, Job[]> = {
      active: [],
      contingent: [],
      forecasted: [],
      evergreen: [],
    };
    for (const job of jobs) {
      const type = (job.posting_type ?? 'active') as PostingType;
      if (grouped[type]) grouped[type].push(job);
    }
    return grouped;
  }, [jobs]);

  return (
    <div>
      {loading ? (
        <div role="status" className="text-muted-foreground">Loading positions…</div>
      ) : loadError ? (
        <LoadErrorState
          what="positions"
          message="Please try again. Positions may still be available."
          onRetry={() => setAttempt(value => value + 1)}
        />
      ) : jobs.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-16 text-center">
            <Briefcase className="h-10 w-10 mx-auto mb-4 text-muted-foreground/50" />
            <p className="text-foreground font-medium mb-2">
              No open positions right now
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              Join our AI Innovation Network to be notified about future openings.
            </p>
            <Button asChild variant="outline">
              <Link to="/ai-network">Join the AI Innovation Network</Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-10">
          {POSTING_TYPE_ORDER.map((t) => {
            const list = byType[t];
            if (list.length === 0) return null;
            const meta = getPostingMeta(t);
            return (
              <section key={t} id={t}>
                <div className="flex items-baseline justify-between gap-3 mb-3 pb-2 border-b border-border">
                  <div className="flex items-baseline gap-2">
                    <h3 className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
                      {meta.label}
                    </h3>
                    <span className="text-xs text-muted-foreground/60">
                      {list.length}
                    </span>
                  </div>
                  <p className="hidden md:block text-xs text-muted-foreground truncate">
                    {SECTION_BLURBS[t]}
                  </p>
                </div>
                <div className="divide-y divide-border">
                  {list.map((job) => (
                    <Link
                      key={job.id}
                      to={`/careers/jobs/${job.slug}`}
                      className="group block px-4 py-3 hover:bg-muted/40 transition-colors"
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="text-base font-medium text-foreground group-hover:text-primary transition-colors truncate">
                              {job.title}
                            </h4>
                            {job.employment_type && (
                              <Badge variant="secondary" className="font-normal text-[10px] px-1.5 py-0 h-4">
                                {job.employment_type}
                              </Badge>
                            )}
                            {job.clearance_required &&
                              job.clearance_required !== 'None' && (
                                <Badge
                                  variant="outline"
                                  className="font-normal gap-1 text-[10px] px-1.5 py-0 h-4"
                                >
                                  <Shield className="h-2.5 w-2.5" />
                                  {job.clearance_required}
                                </Badge>
                              )}
                          </div>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5 text-xs text-muted-foreground">
                            {(job.location || job.location_type) && (
                              <span className="inline-flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {[job.location, job.location_type]
                                  .filter(Boolean)
                                  .join(' • ')}
                              </span>
                            )}
                            {formatPayRange(job.public_pay_min, job.public_pay_max, job.public_pay_interval) && (
                              <span>{formatPayRange(job.public_pay_min, job.public_pay_max, job.public_pay_interval)}</span>
                            )}
                            {meta.alwaysShowContingencyNote &&
                              (job.contingency_note || job.expected_start_window) && (
                                <span className="italic">
                                  {[job.contingency_note, job.expected_start_window]
                                    .filter(Boolean)
                                    .join(' • ')}
                                </span>
                              )}
                          </div>
                        </div>
                        <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all shrink-0" />
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PublicJobsList;
